import { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { decidePlatformCharge, resolveDeliveryPayable, resolveInterBusinessCharge } from '../api/financeApi';
import { money } from '../format';

// Generalised "record a payment" form (spec §A6). Every "Mark paid" across Visit fees / Delivery
// payments / B2B billing opens this same modal — method dropdown + REQUIRED reference + amount +
// date + note — and submits { action: 'mark_paid', method, reference, amount, note }.
//
//  target = { kind, id, amount, title }
//    kind: 'platformCharge' | 'deliveryPayable' | 'interBusinessCharge' | 'booking'
const METHODS = [
  { value: 'UPI', label: 'UPI' },
  { value: 'BANK_TRANSFER', label: 'Bank transfer' },
  { value: 'CASH', label: 'Cash' },
  { value: 'CARD', label: 'Card' },
  { value: 'NET_BANKING', label: 'Net banking' },
  { value: 'WALLET', label: 'Wallet' },
  { value: 'OTHER', label: 'Other' }
];

const SUBMITTERS = {
  platformCharge: (id, payload) => decidePlatformCharge(id, payload),
  deliveryPayable: (id, payload) => resolveDeliveryPayable(id, payload),
  interBusinessCharge: (id, payload) => resolveInterBusinessCharge(id, payload)
};

export default function MarkPaymentModal({ target, show, onHide, onRecorded, asAdmin = false }) {
  const [method, setMethod] = useState('UPI');
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [receivedAt, setReceivedAt] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show) return;
    setMethod('UPI');
    setAmount(target?.amount != null ? String(target.amount) : '');
    setReference('');
    setReceivedAt('');
    setNote('');
    setError('');
  }, [show, target]);

  if (!target) return null;

  const submitter = target.onSubmit || SUBMITTERS[target.kind];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (reference.trim().length < 2) {
      setError('Enter the payment reference / transaction id (for cash, the receipt or acknowledgement no.).');
      return;
    }
    if (!submitter) {
      setError('This payment type cannot be recorded here.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const payload = {
        action: 'mark_paid',
        method,
        reference: reference.trim(),
        note: note.trim()
      };
      if (amount !== '') payload.amount = Number(amount);
      if (receivedAt) payload.receivedAt = new Date(receivedAt).toISOString();
      const updated = await submitter(target.id, payload);
      onRecorded?.(updated);
    } catch (err) {
      setError(err?.response?.data?.msg || 'Unable to record this payment.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered fullscreen="sm-down">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title as="h5">Mark paid{target.title ? ` — ${target.title}` : ''}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {target.amount != null && (
            <p className="small mb-2"><span className="text-muted">Amount owed:</span> <strong>{money(target.amount)}</strong></p>
          )}
          {error && <Alert variant="danger" className="py-2">{error}</Alert>}

          <Form.Group className="mb-3" controlId="mark-paid-method">
            <Form.Label>Payment method</Form.Label>
            <Form.Select value={method} onChange={(e) => setMethod(e.target.value)}>
              {METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3" controlId="mark-paid-amount">
            <Form.Label>Amount (₹)</Form.Label>
            <Form.Control type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Form.Group>

          <Form.Group className="mb-3" controlId="mark-paid-date">
            <Form.Label>Date received (optional — blank for today)</Form.Label>
            <Form.Control type="date" value={receivedAt} onChange={(e) => setReceivedAt(e.target.value)} />
          </Form.Group>

          <Form.Group className="mb-3" controlId="mark-paid-reference">
            <Form.Label>
              {method === 'CASH' ? 'Cash receipt / acknowledgement no.' : 'Reference / transaction id'}{' '}
              <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control type="text" required minLength={2} maxLength={200} value={reference} onChange={(e) => setReference(e.target.value)} />
            <Form.Text className="text-muted">Required — a payment is only recorded with a method and a reference.</Form.Text>
          </Form.Group>

          <Form.Group controlId="mark-paid-note">
            <Form.Label>Note (optional)</Form.Label>
            <Form.Control as="textarea" rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={submitting}>Cancel</Button>
          <Button variant="success" type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Confirm payment'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
